import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo } from 'react'
import { type ActionItem, EmptyState, Table } from 'shared/ui'
import { NoResultsFound } from 'shared/ui/NoResultsFound/NoResultsFound'
import { type IResponseModifyCampaignContactState } from 'entities/Campaign'
import { ContactStatisticBulkActions } from 'widgets/ContactStatistic'
import { type CampaignContactsStatisticsTableStore } from 'pages/campaigns/ui/detail/CampaignContactsStatisticsTable'
import { useCampaignDetailContext } from 'pages/campaigns/ui/detail/CampaignDetail/context/context'
import styles from './styles.module.scss'
import { getColumns } from './getColumns'

type ICampaignContactsStatisticsProps = {
  store: CampaignContactsStatisticsTableStore
  activeFilter?: IResponseModifyCampaignContactState
}

export const CampaignContactsStatisticsTable = observer(
  ({ store, activeFilter }: ICampaignContactsStatisticsProps) => {
    const {
      items,
      total,
      loading,
      onPaginationModelChange,
      tableStore,
      contactsTagsModalStore,
      scrollToTopTrigger,
      page,
      limit,
      onSuccessDelete,
      search,
      onOpenContactsDetails,
      isNoData,
      variant,
      isShowReply,
      contactsDetailsStore,
      status,
      removeExecutions,
    } = store

    const { setContactsDetailsStore } = useCampaignDetailContext()

    useEffect(() => {
      tableStore.resetSelected()
    }, [activeFilter])

    useEffect(() => {
      setContactsDetailsStore(contactsDetailsStore)
    }, [contactsDetailsStore])

    const columns = useMemo(
      () => getColumns(onOpenContactsDetails, variant, isShowReply, activeFilter),
      [onOpenContactsDetails, isShowReply, activeFilter]
    )

    const additionActions: ActionItem[] = useMemo(() => {
      if (variant === 'overview' && status === 'active' && activeFilter === 'in_progress') {
        const onRemove = () => removeExecutions(tableStore.selectedRows)
        return [
          {
            icon: 'minusCircle',
            text: 'Remove from campaign',
            onAction: onRemove,
          },
        ]
      }
      return []
    }, [variant, status, activeFilter])

    if (isNoData) {
      return (
        <EmptyState
          text={'No contacts found'}
          noIcon
          boxProps={{ flexGrow: 1, height: undefined }}
        />
      )
    }

    if (!loading && !items.length) {
      return <NoResultsFound boxProps={{ flexGrow: 1, height: undefined }} />
    }

    return (
      <div className={styles.wrapTable}>
        <Table
          ariaLabel={'CampaignContactsStatisticsTable'}
          columns={columns}
          rows={items}
          rowsCount={total}
          isLoading={loading}
          onChangePagination={onPaginationModelChange}
          initPage={page}
          limit={limit}
          startPaginationSize={10}
          sxTable={{ marginRight: 2, width: 'calc(100% - 16px)' }}
          scrollToTOpTrigger={scrollToTopTrigger}
          withCheckbox
          store={tableStore}
          headerLeftContentProps={{
            actionsContent: (
              <ContactStatisticBulkActions
                additionActions={additionActions}
                contactsTagsModalStore={contactsTagsModalStore}
                selectedContactIds={tableStore.selectedRows.map((row) => row.contact.id)}
                search={search}
                onSuccessDelete={onSuccessDelete}
                withNewActions
              />
            ),
          }}
        />
      </div>
    )
  }
)
